import { formatCOP, formatDistance, formatFecha } from './cn';
import type { PedidoDetalle } from './queries';

const METODOS: Record<string, string> = {
  CASH: 'Efectivo',
  CARD: 'Tarjeta',
  PSE: 'PSE',
  NEQUI: 'Nequi',
  WALLET: 'Billetera',
};

/**
 * Abre el comprobante del pedido listo para imprimir o guardar como PDF.
 *
 * Se arma en el navegador con los datos que ya trajo el detalle: no hace falta
 * un generador de PDF en el servidor para que el cliente tenga su soporte del
 * gasto. El diálogo de impresión de cualquier navegador permite «Guardar como
 * PDF».
 */
export function abrirComprobante(pedido: PedidoDetalle): void {
  const ventana = window.open('', '_blank', 'width=720,height=900');
  if (!ventana) return; // El navegador bloqueó la ventana emergente.

  ventana.document.write(html(pedido));
  ventana.document.close();
  ventana.focus();
}

function html(p: PedidoDetalle): string {
  const lineas = p.items
    .map(
      (item) => `
        <tr>
          <td>${item.quantity}</td>
          <td>
            ${escapar(item.productName)}
            ${item.options.length ? `<div class="sub">${escapar(item.options.map((o) => o.optionName).join(' · '))}</div>` : ''}
          </td>
          <td class="der">${formatCOP(item.unitPrice)}</td>
          <td class="der">${formatCOP(item.lineTotal)}</td>
        </tr>`,
    )
    .join('');

  const fila = (etiqueta: string, valor: string, fuerte = false): string =>
    `<tr class="${fuerte ? 'total' : ''}"><td colspan="3" class="der">${etiqueta}</td><td class="der">${valor}</td></tr>`;

  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>Comprobante ${escapar(p.code)} — Kick Domicilios</title>
<style>
  @page { margin: 16mm; }
  body { font: 14px/1.5 system-ui, -apple-system, "Segoe UI", sans-serif; color: #1a1a1a; max-width: 720px; margin: 0 auto; padding: 24px; }
  header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #e11d2e; padding-bottom: 12px; }
  h1 { font-size: 22px; margin: 0; }
  .marca { font-size: 20px; font-weight: 800; color: #e11d2e; letter-spacing: -0.5px; }
  .gris { color: #6b7280; font-size: 13px; }
  h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 0.06em; color: #6b7280; margin: 24px 0 8px; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: 12px; text-transform: uppercase; color: #6b7280; border-bottom: 1px solid #e5e7eb; padding: 6px 4px; }
  td { padding: 6px 4px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
  .der { text-align: right; white-space: nowrap; }
  .sub { font-size: 12px; color: #6b7280; }
  .total td { border-top: 2px solid #1a1a1a; border-bottom: none; font-size: 17px; font-weight: 700; padding-top: 10px; }
  dl { display: grid; grid-template-columns: max-content 1fr; gap: 4px 16px; margin: 0; }
  dt { color: #6b7280; }
  dd { margin: 0; }
  footer { margin-top: 32px; border-top: 1px solid #e5e7eb; padding-top: 12px; font-size: 12px; color: #6b7280; }
  @media print { body { padding: 0; } .no-imprimir { display: none; } }
  .boton { display: inline-block; margin-top: 24px; background: #e11d2e; color: #fff; border: 0; border-radius: 8px; padding: 10px 18px; font-size: 14px; font-weight: 600; cursor: pointer; }
</style>
</head>
<body>
  <header>
    <div>
      <h1>Comprobante ${escapar(p.code)}</h1>
      <div class="gris">${formatFecha(p.createdAt)}</div>
    </div>
    <div class="marca">Kick Domicilios</div>
  </header>

  <h2>${p.merchantName ? 'Comercio' : 'Recogida'}</h2>
  <div>${escapar(p.merchantName ?? p.pickupAddress.addressLine)}</div>

  <h2>Entrega</h2>
  <dl>
    <dt>Dirección</dt><dd>${escapar(p.deliveryAddress.addressLine)}</dd>
    ${p.deliveryAddress.details ? `<dt>Detalles</dt><dd>${escapar(p.deliveryAddress.details)}</dd>` : ''}
    <dt>Ciudad</dt><dd>${escapar(p.deliveryAddress.city)}</dd>
    <dt>Distancia</dt><dd>${formatDistance(p.distanceMeters)}</dd>
    ${p.deliveredAt ? `<dt>Entregado</dt><dd>${formatFecha(p.deliveredAt)}</dd>` : ''}
  </dl>

  <h2>Detalle</h2>
  <table>
    <thead>
      <tr><th>Cant.</th><th>Producto</th><th class="der">Unitario</th><th class="der">Total</th></tr>
    </thead>
    <tbody>
      ${lineas}
      ${fila('Subtotal', formatCOP(p.subtotal))}
      ${fila('Domicilio', p.deliveryFee === 0 ? 'Gratis' : formatCOP(p.deliveryFee))}
      ${p.serviceFee > 0 ? fila('Tarifa lluvia', formatCOP(p.serviceFee)) : ''}
      ${p.tip > 0 ? fila('Propina', formatCOP(p.tip)) : ''}
      ${p.discountTotal > 0 ? fila('Descuento', `-${formatCOP(p.discountTotal)}`) : ''}
      ${fila('Total', formatCOP(p.total), true)}
    </tbody>
  </table>

  <h2>Pago</h2>
  <dl>
    <dt>Método</dt><dd>${escapar(METODOS[p.paymentMethod] ?? p.paymentMethod)}</dd>
    <dt>Estado</dt><dd>${p.paymentStatus === 'PAID' ? 'Pagado' : 'Pendiente'}</dd>
  </dl>

  <button class="boton no-imprimir" onclick="window.print()">Imprimir o guardar como PDF</button>

  <footer>
    Documento generado por Kick Domicilios. No es una factura electrónica con
    validez tributaria; es el soporte del pedido ${escapar(p.code)}.
  </footer>
</body>
</html>`;
}

/** El nombre de un producto o una dirección pueden traer caracteres de HTML. */
function escapar(texto: string): string {
  return texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
